import { z } from "zod";

// Email validation
export const emailSchema = z
  .string()
  .trim()
  .min(1, "Email is required")
  .email("Please enter a valid email")
  .max(255, "Email is too long");

// Password validation
export const passwordSchema = z
  .string()
  .min(6, "Password must be at least 6 characters")
  .max(128, "Password is too long");

// Name validation
export const nameSchema = z
  .string()
  .trim()
  .min(1, "Name is required")
  .max(50, "Name must be less than 50 characters")
  .regex(/^[a-zA-Z\s'-]+$/, "Name contains invalid characters");

// Bio validation
export const bioSchema = z
  .string()
  .trim()
  .max(500, "Bio must be less than 500 characters")
  .optional();

// Age validation
export const ageSchema = z
  .number()
  .int("Age must be a whole number")
  .min(18, "You must be at least 18 years old")
  .max(120, "Please enter a valid age")
  .optional();

// Message validation
export const messageSchema = z
  .string()
  .trim()
  .min(1, "Message cannot be empty")
  .max(5000, "Message is too long");

// Arena name validation
export const arenaNameSchema = z
  .string()
  .trim()
  .min(3, "Arena name must be at least 3 characters")
  .max(100, "Arena name must be less than 100 characters");

// Topic validation  
export const topicSchema = z
  .string()
  .trim()
  .min(3, "Topic must be at least 3 characters")
  .max(200, "Topic must be less than 200 characters");

// Report reason validation
export const reportReasonSchema = z
  .string()
  .trim()
  .min(10, "Please provide more details (at least 10 characters)")
  .max(1000, "Report is too long");

// URL validation
export const urlSchema = z
  .string()
  .url("Please enter a valid URL")
  .optional()
  .or(z.literal(""));

// Phone validation (basic)
export const phoneSchema = z
  .string()
  .regex(/^\+?[1-9]\d{1,14}$/, "Please enter a valid phone number")
  .optional();

// Profile form schema
export const profileFormSchema = z.object({
  name: nameSchema,
  age: ageSchema,
  bio: bioSchema,
  gender: z.enum(["male", "female", "non_binary", "other", "prefer_not_to_say"]).optional(),
  location: z.string().trim().max(100).optional(),
  religion: z.string().trim().max(50).optional(),
});

// Auth form schema
export const authFormSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
});

// Arena creation schema
export const createArenaSchema = z.object({
  name: arenaNameSchema,
  topic: topicSchema,
  description: z.string().trim().max(500).optional(),
  language: z.string().min(2).max(10),
  max_participants: z.number().int().min(2).max(100),
  is_video_enabled: z.boolean(),
  is_voice_only: z.boolean(),
});

// Helper to get first validation error message
export function getFirstError(errors: z.ZodError): string {
  return errors.errors[0]?.message || "Validation failed";
}

// Safe validate function that returns { success, data, error }
export function safeValidate<T>(
  schema: z.ZodSchema<T>,
  data: unknown
): { success: true; data: T } | { success: false; error: string } {
  const result = schema.safeParse(data);
  if (result.success) {
    return { success: true, data: result.data };
  }
  return { success: false, error: getFirstError(result.error) };
}
